// components/AssociateModal.jsx
import React, { useState } from "react";
import axios from "axios";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  qualification: "",
  residence: "",
  workExperience: "",
  employmentType: "Full Time",
  expectedSalary: "",
};

const AssociateModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetAndClose = () => {
    setForm(initialForm);
    setError("");
    setSuccess("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const requiredFields = [
      "name",
      "email",
      "phone",
      "qualification",
      "residence",
      "employmentType",
      "expectedSalary",
    ];

    const missingField = requiredFields.find((field) => !form[field]?.trim());
    if (missingField) {
      setError(`Please fill in the "${missingField}" field.`);
      return;
    }

    try {
      setLoading(true);
      
      // ✅ Log the request for debugging
      console.log("Submitting form data:", form);
      console.log("API URL:", `${process.env.REACT_APP_API_URL}/associate/create-associate`);
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/associate/create-associate`,
        form,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000, // 10 second timeout
        }
      );

      console.log("Response:", response.data);

      if (response.data.success) {
        setSuccess("Your application has been submitted successfully!");
        setForm(initialForm);
        setTimeout(() => {
          resetAndClose();
        }, 2000);
      } else {
        setError(response.data.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Error submitting associate application:", err);
      
      // ✅ Better error handling
      if (err.code === 'ERR_NETWORK') {
        setError("Cannot connect to server. Please check if the server is running.");
      } else if (err.response) {
        // Server responded with error
        console.error("Response error:", err.response.data);
        setError(err.response.data.message || "Server error. Please try again.");
      } else if (err.request) {
        // Request made but no response
        console.error("No response:", err.request);
        setError("No response from server. Please check your connection.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
        onClick={resetAndClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
        <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
          <button
            onClick={resetAndClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-[#101828] text-2xl leading-none"
            aria-label="Close associate modal"
          >
            &times;
          </button>

          <h2 className="text-2xl font-extrabold text-[#101828] mb-1">
            Become an Associate
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Apply to become an associate with us. Fill in your details below.
          </p>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm rounded-lg px-4 py-2 mb-3 border border-red-200">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 text-green-600 text-sm rounded-lg px-4 py-2 mb-3 border border-green-200">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-[#101828]">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                  placeholder="Your full name"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#101828]">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#101828]">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                  placeholder="Phone number"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-[#101828]">
                  Education Qualification *
                </label>
                <input
                  type="text"
                  name="qualification"
                  value={form.qualification}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                  placeholder="e.g. B.Tech in Computer Science, MBA"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-[#101828]">
                  Residence Address *
                </label>
                <textarea
                  name="residence"
                  value={form.residence}
                  onChange={handleChange}
                  rows={2}
                  className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                  placeholder="Your current residence address"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-[#101828]">
                  Work Experience
                </label>
                <textarea
                  name="workExperience"
                  value={form.workExperience}
                  onChange={handleChange}
                  rows={2}
                  className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                  placeholder="Describe your previous work experience (if any)"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#101828]">
                  Employment Type *
                </label>
                <select
                  name="employmentType"
                  value={form.employmentType}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                  required
                >
                  <option value="Full Time">Full Time</option>
                  <option value="Part Time">Part Time</option>
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold text-[#101828]">
                  Expected Salary / Package *
                </label>
                <input
                  type="text"
                  name="expectedSalary"
                  value={form.expectedSalary}
                  onChange={handleChange}
                  className="w-full mt-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F9BD6]"
                  placeholder="e.g. 25,000/month or 3 LPA"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={resetAndClose}
                className="px-6 py-2 rounded-full font-semibold text-[#101828] border border-gray-200 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 rounded-full font-semibold text-white bg-[#2F9BD6] hover:bg-[#218ac0] transition disabled:opacity-60"
              >
                {loading ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AssociateModal;